/**
 * Camada de acesso a dados do protótipo.
 *
 * 🛑 **Esta é a fronteira descartável.** Nenhum componente de apresentação
 * importa `fixtures/` diretamente — todos passam por aqui. No Epic 3, apenas
 * este arquivo é reescrito para consumir a API real, e nenhuma tela muda.
 *
 * A assinatura das funções já é assíncrona, embora os dados sintéticos sejam
 * síncronos: é o formato que a API terá, e adotá-lo agora evita que as telas
 * precisem ser reescritas quando a origem mudar.
 */

import { generateFixtures } from "../fixtures/index.js";
import type {
  AdvisorCandidate,
  BenchRun,
  CacheEffectiveness,
  CalibrationResult,
  EngineMetric,
  GpuMetric,
  HardwareInfo,
  RequestSample,
  SampleDrop,
  Scenario,
  ScenarioCapacity,
  ScenarioStats,
} from "../fixtures/index.js";

// O conjunto é gerado uma vez e reaproveitado: regerar a cada consulta
// produziria telas diferentes na mesma sessão, apesar da semente fixa.
const set = generateFixtures();

/** Simula a latência de uma chamada de rede, para que as telas exercitem carregamento. */
function delayed<T>(value: T, ms = 120): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

/** Lista as execuções, da mais recente para a mais antiga. */
export function listRuns(): Promise<readonly BenchRun[]> {
  return delayed([...set.runs].reverse());
}

/** Detalha uma execução. */
export function getRun(id: number): Promise<BenchRun | undefined> {
  return delayed(set.runs.find((r) => r.id === id));
}

/** Lista os cenários de uma execução, ou todos quando o filtro é omitido. */
export function listScenarios(runId?: number): Promise<readonly Scenario[]> {
  const rows = runId === undefined ? set.scenarios : set.scenarios.filter((s) => s.runId === runId);
  return delayed(rows);
}

/** Estatísticas agregadas de um cenário, já calculadas pela camada de origem. */
export function getScenarioStats(scenarioId: number): Promise<ScenarioStats | undefined> {
  return delayed(set.stats.find((s) => s.scenarioId === scenarioId));
}

/** Métricas de capacidade e eficiência de um cenário — as que decidem a compra. */
export function getScenarioCapacity(scenarioId: number): Promise<ScenarioCapacity | undefined> {
  return delayed(set.capacity.find((c) => c.scenarioId === scenarioId));
}

/** Capacidade de todos os cenários, para comparação entre candidatos. */
export function listCapacity(): Promise<readonly ScenarioCapacity[]> {
  return delayed(set.capacity);
}

/** Todas as estatísticas, para as telas de comparação. */
export function listStats(): Promise<readonly ScenarioStats[]> {
  return delayed(set.stats);
}

/** Amostras individuais de um cenário. É a granularidade que sustenta os percentis. */
export function listSamples(scenarioId: number): Promise<readonly RequestSample[]> {
  return delayed(set.samples.filter((s) => s.scenarioId === scenarioId));
}

/** Séries temporais de telemetria de acelerador. */
export function listGpuMetrics(scenarioId: number): Promise<readonly GpuMetric[]> {
  return delayed(set.gpuMetrics.filter((m) => m.scenarioId === scenarioId));
}

/** Séries temporais das métricas internas do motor. */
export function listEngineMetrics(scenarioId: number): Promise<readonly EngineMetric[]> {
  return delayed(set.engineMetrics.filter((m) => m.scenarioId === scenarioId));
}

/** Eficácia dos caches de saída de um cenário. */
export function listCacheEffectiveness(scenarioId: number): Promise<readonly CacheEffectiveness[]> {
  return delayed(set.cacheEffectiveness.filter((c) => c.scenarioId === scenarioId));
}

/** Descartes de amostra contabilizados num cenário. */
export function listDrops(scenarioId: number): Promise<readonly SampleDrop[]> {
  return delayed(set.drops.filter((d) => d.scenarioId === scenarioId));
}

/** Calibração de telemetria de uma execução. */
export function getCalibration(runId: number): Promise<CalibrationResult | undefined> {
  return delayed(set.calibrations.find((c) => c.runId === runId));
}

/** Inventário de hardware detectado, com consumo. */
export function getHardware(): Promise<HardwareInfo> {
  return delayed(set.hardware);
}

/** Candidatos de compra avaliados. */
export function listAdvisorCandidates(): Promise<readonly AdvisorCandidate[]> {
  return delayed(set.advisor);
}

/** Acesso síncrono ao conjunto, para composições que não valem uma promessa. */
export const snapshot = set;
